import {
  boardOf,
  can,
  childCount,
  fmtDate,
  formatReport,
  githubRef,
  HIT_ROLES,
  assigneeNames,
  flowLane,
  KINDS,
  lanesFor,
  linkLabel,
  draftHiddenFor,
  draftShows,
  ownBoardOnly,
  prDescription,
  projectCode,
  TAGS,
  teamsIn,
  type Board,
  type BoardItem,
  type Lane,
  type Stage,
} from "@boem/core/shared";
import { Fragment, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { api, ApiError } from "../api.ts";
import { groupItems, groupKey, LOOK_DEFAULT, saveLook, savedLook, startLook, toggleFold, writeLook, type BoardLook, type GroupBy } from "../grouping.ts";
import type { PanelTarget } from "../panels.ts";
import {
  activeFilterCount,
  chipProject,
  withFilters,
  clearedFilters,
  hasAi,
  initialFilters,
  matches,
  readDrafts,
  readDue,
  readProjectView,
  readQueues,
  saveProjectView,
  savedProjectView,
  sortItems,
  withoutPopoverFilters,
  writeDrafts,
  writeDue,
  writeProjectView,
  writeQueues,
  type ProjectView,
  type Filters,
  type Sort,
} from "../filters.ts";
import { AiChip, Avatar, JumpContext } from "../util.tsx";
import { BoardDayContext, DueModal } from "./Due.tsx";
import { ViewModeBar } from "./Views.tsx";
import { quickItems, sameSpec, type ViewSpec } from "@boem/core/views";
import { specOf, type OpenView } from "../views.ts";
import { isOverdue, isDueThisWeek } from "@boem/core/due";
import { Card, type CardAction } from "./Card.tsx";
import { MastExtras, RequestModal } from "./ForYou.tsx";
import { AvatarMenu, boardChips, boardName, CountChips, DUE_LABEL, FilterPanel, FiltersMenu, NOTES_LABEL, NotesToggle, PanelFilterRow, Ribbon, SORT_LABEL, TEAMS_LABEL, type CountChip } from "./Header.tsx";
import { NotesContext, NotesThread, threadMeta } from "./Notes.tsx";
import { AssigneeFilter } from "./AssigneeFilter.tsx";
import { GroupHead, GroupSelect } from "./Groups.tsx";
import { HistoryList, PanelHeader, type PanelHeadProps } from "./PanelHead.tsx";
import { Icon } from "./Icons.tsx";
import { GoToToolbarButton } from "./QuickGoTo.tsx";
import { CloseModal, EditItemModal, HistoryModal, NewItemModal, NoteModal, StageModal } from "./ItemModals.tsx";
import { Lightbox } from "./Lightbox.tsx";
import { AddLinkModal, linkNo } from "./Links.tsx";
import { AttachModal, ShotStrip } from "./Media.tsx";
import { Modal } from "./Modal.tsx";
import { describeMoves, itemNo, OrganizeModal, projectRange } from "./Structure.tsx";
import { TeamAdminModal } from "./TeamAdmin.tsx";
import { mediaApi } from "../mediaApi.ts";
import { readAttach, withoutParam } from "../uploadLink.ts";
import { readOrgSetting, writeOrgSetting } from "../org.ts";
import { currentListing, useBoards } from "../boards.ts";
import { BoardSwitcher, EmptyBoard, PrivateChip } from "./Boards.tsx";
import { CopyToMineModal, FrozenBanner, PlatformModal } from "./Lifecycle.tsx";
import { bridgeApi, CopyFromCompanyModal, CreateCompanyModal, LinkModal, SendUpdatesModal } from "./Bridges.tsx";
import { LinkPrModal, ProjectSettingsModal, UnlinkedPrs } from "./GitHub.tsx";
import { githubApi } from "../prs.ts";

type Dialog =
  /** `noteId`: the Notes dialog opens with that note of yours being edited */
  | { kind: "note" | "close" | "history" | "organize" | "link" | "edit"; num: number; noteId?: string }
  | { kind: "shot"; num: number; index: number }
  | { kind: "attach"; num: number; files?: File[]; noteId?: string | null }
  | { kind: "stage"; num: number; to: Stage }
  /** a new item; under `parent` when logged from a project or as a sub-item; `project`: a new project */
  | { kind: "new"; parent?: number; feature?: boolean; project?: boolean }
  | { kind: "team" }
  /** Copy to my board (orgs.md 1.5; from OEMUAP, 6.2) */
  | { kind: "copy"; num: number }
  /** a personal item and OEMUAP (orgs.md 6): link it to an OEMUAP item, or create one from it */
  | { kind: "bridgeLink" | "bridgeCreate" | "bridgeSend"; num: number }
  /** a platform admin's boards that need an owner */
  | { kind: "platform" }
  /** Request a feature as a GitHub issue (when the board has no project of its own for them) */
  | { kind: "request" }
  | { kind: "report"; text: string }
  /** GitHub (docs/github.md): link a PR to an item by its address; a project's repos */
  | { kind: "linkPr" | "projectSettings"; num: number }
  /** its due date (OEM-267) */
  | { kind: "due"; num: number };

/** the header's popovers: the Filters popover and the avatar menu */
type Menu = "filters" | "me" | null;

/** "Who reviewed" (the reviewer pills) is folded away until someone opens it; remembered per browser. */
function savedWhoOpen(): boolean {
  try {
    return readOrgSetting("buyoem.who") === "1";
  } catch {
    return false;
  }
}

/** The ☰ button: opens the boards and projects list (a drawer under 900px). */
export function MenuButton({ open, onClick }: { open: boolean; onClick: () => void }) {
  return (
    <button type="button" className="sb-toggle" aria-label="Boards and projects" aria-controls="sidebar" aria-expanded={open} onClick={onClick}>
      <Icon name="menu" />
    </button>
  );
}

/** What a panel (docs/design 4.3) shows in its head: everything but what the view itself knows. */
export type PanelInfo = Omit<PanelHeadProps, "item" | "meta" | "onOpenAsBoard"> & {
  /** a project panel: make it the board, with this panel's filters */
  onOpenAsBoard?: (f: Filters) => void;
};

/** The main board's grouping in the URL (null: taken out), without a new history entry. */
function putLook(look: BoardLook | null) {
  const { pathname, search, hash } = window.location;
  const next = writeLook(search, look);
  if (next !== search) window.history.replaceState(window.history.state, "", pathname + next + hash);
}

/** The board's Due filter in the URL (null: taken out), without a new history entry. */
function putDue(due: Filters["due"]) {
  const { pathname, search, hash } = window.location;
  const next = writeDue(search, due);
  if (next !== search) window.history.replaceState(window.history.state, "", pathname + next + hash);
}

/** The Drafts filter in the URL (null or "hide": taken out), without a new history entry (OEM-286). */
function putDrafts(d: Filters["drafts"] | null) {
  const { pathname, search, hash } = window.location;
  const next = writeDrafts(search, d);
  if (next !== search) window.history.replaceState(window.history.state, "", pathname + next + hash);
}

/** The Project filter's No project and Hide in the URL (null: taken out), without a new history entry (OEM-260). */
function putProjectView(v: ProjectView | null) {
  const { pathname, search, hash } = window.location;
  const next = writeProjectView(search, v);
  if (next !== search) window.history.replaceState(window.history.state, "", pathname + next + hash);
}

/** The board's queues in the URL (null: taken out), without a new history entry. */
function putQueues(lanes: Set<Lane> | null) {
  const { pathname, search, hash } = window.location;
  const next = writeQueues(search, lanes);
  if (next !== search) window.history.replaceState(window.history.state, "", pathname + next + hash);
}

/** Marks a top-level card in a column (just after it), so the bridge and "scroll to it" find it. */
const Slot = ({ num }: { num: number }) => <i hidden data-card={num} />;

interface Props {
  board: Board;
  /** the board (everything in progress, plus closed), the archive (verified and archived items), or
   *  one panel of the cascade beside the board: a project's items, or one item with its history */
  mode?: "board" | "archive" | "panel";
  /** which column this is: 0 the board, 1, 2, ... the panels right of it */
  column?: number;
  /** open an item or a project in a panel right of this column; `source` is the card or row clicked */
  onPanel?: (target: PanelTarget, source: { num: number; via?: number }) => void;
  /** panel mode: what it shows and its head's buttons */
  panel?: PanelInfo;
  /** the filters to open with ("Open as board" carries a panel's over) */
  initial?: Partial<Filters>;
  /** on the header line while a panel is open: close every unpinned panel */
  onReset?: () => void;
  refresh: () => Promise<void>;
  onSignOut: () => void;
  toast: (msg: string) => void;
  /** an item to scroll to once this view shows (e.g. after switching from the board to the archive) */
  jumpTo?: number | null;
  onJumped?: () => void;
  /** asked to jump to an item this view doesn't have */
  onMissing?: (num: number) => void;
  /** the project whose board this is (null: the main board) */
  project?: BoardItem | null;
  /** show another board - a project's own (its number) or the main one (null) - and jump to an item there */
  onSwitch?: (board: number | null, jumpTo?: number) => void;
  /** the live connection dropped; it reconnects by itself */
  offline?: boolean;
  /** ☰: open the boards and projects list (only shown under 900px, where the list is a drawer) */
  onMenu?: () => void;
  menuOpen?: boolean;
  /** a saved or quick view (OEM-268): the main board's list across every project, with the view's filters */
  view?: OpenView | null;
  /** Save changes (to the open view) or Save as new view, with the board's filters now */
  onSaveView?: (spec: ViewSpec, how: "changes" | "new") => void;
  /** the main board's filters as they are now (what "+ Save current view" in the sidebar saves) */
  onSpec?: (spec: ViewSpec) => void;
}

export function LogView({
  board,
  mode = "board",
  column = 0,
  onPanel,
  panel,
  initial,
  onReset,
  refresh,
  onSignOut,
  toast,
  jumpTo,
  onJumped,
  onMissing,
  project: here = null,
  onSwitch,
  offline = false,
  onMenu,
  menuOpen = false,
  view = null,
  onSaveView,
  onSpec,
}: Props) {
  const { viewer, items: all } = board;
  // which board this is: a personal board leaves out what's OEMUAP's alone (Teams, feature requests, reminders)
  const boards = useBoards();
  const personal = board.org?.kind === "personal";
  const listing = boards ? currentListing(boards.list, boards.org.slug) : null;
  const archive = mode === "archive";
  const inPanel = mode === "panel" && !!panel;
  // a view (OEM-268): the main board's list over every project, those with a board of their own too
  const viewMode = mode === "board" && !here && !!view;
  const [f, setF] = useState<Filters>(() => {
    const base = withFilters(archive ? { ...initialFilters(), lanes: new Set<Lane>() } : initialFilters(), initial);
    // the board (main or a project's): ?queue=review opens straight on the PR review queue
    const fromUrl = mode === "board" && !initial?.lanes && typeof window !== "undefined" ? readQueues(window.location.search) : null;
    // ?due=overdue opens on what's overdue (OEM-267)
    const dueUrl = mode === "board" && !initial?.due && typeof window !== "undefined" ? readDue(window.location.search) : null;
    // No project and Hide (OEM-260): the main board and the archive open with the URL's (?noproject=1,
    // ?hide=175), else the ones this person left them in; a view opens with its own
    const ownView = (mode === "board" || mode === "archive") && !here && !view && initial?.noProject === undefined && initial?.hideProjects === undefined;
    const pv = ownView && typeof window !== "undefined" ? (readProjectView(window.location.search) ?? savedProjectView(viewer.id, archive)) : null;
    // ?drafts=show opens with everyone's drafts (OEM-286)
    const draftsUrl = mode === "board" && !initial?.drafts && typeof window !== "undefined" ? readDrafts(window.location.search) : null;
    return { ...base, ...(fromUrl ? { lanes: fromUrl } : {}), ...(dueUrl ? { due: dueUrl } : {}), ...(pv && base.project == null ? pv : {}), ...(draftsUrl ? { drafts: draftsUrl } : {}) };
  });
  // today on the board (its time zone): what the Due filter, the due badges and the counts go by
  const { today } = useContext(BoardDayContext);
  const [qDraft, setQDraft] = useState(initial?.q ?? "");
  const [open, setOpen] = useState<Menu>(null);
  const [whoOpen, setWhoOpen] = useState(savedWhoOpen);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  // notes on every card: the toolbar toggle (App keeps it, per person)
  const notesView = useContext(NotesContext);
  const [flash, setFlash] = useState<number | null>(null);
  const pendingJump = useRef<number | null>(null);
  // the main board (not a project's own, the archive or a panel): a flat list where each item in a
  // project carries its chip, or grouped by project or person (Group by, with folding headings)
  const mainBoard = mode === "board" && !here;
  const [look, setLookState] = useState<BoardLook>(() =>
    // a view opens with its own Group by; the main board with the URL's, else the one this person left it in
    view && mainBoard
      ? { ...savedLook(viewer.id), group: view.spec.group }
      : mainBoard
        ? startLook(typeof window === "undefined" ? "" : window.location.search, savedLook(viewer.id))
        : LOOK_DEFAULT,
  );
  /** a new grouping or fold: remembered for this person on this device (the URL follows below); a view's isn't */
  const setLook = (next: BoardLook) => {
    setLookState(next);
    if (!view) saveLook(viewer.id, next);
  };
  // the grouping and the folds are in the URL while the main board shows, so a link reproduces the view
  useEffect(() => {
    if (!mainBoard) return;
    putLook(look);
  }, [mainBoard, look]);
  useEffect(() => (mainBoard ? () => putLook(null) : undefined), [mainBoard]);
  // the queues too (left out while they're the default), so a link to the PR review queue opens on it
  useEffect(() => {
    if (mode !== "board") return;
    putQueues(f.lanes);
  }, [mode, f.lanes]);
  useEffect(() => (mode === "board" ? () => putQueues(null) : undefined), [mode]);
  // the Due filter too (?due=overdue), so a link to what's overdue opens on it
  useEffect(() => {
    if (mode !== "board") return;
    putDue(f.due);
  }, [mode, f.due]);
  useEffect(() => (mode === "board" ? () => putDue(null) : undefined), [mode]);
  // the Drafts filter too (?drafts=show), so a link with everyone's drafts opens with them (OEM-286)
  useEffect(() => {
    if (mode !== "board" || column !== 0) return;
    putDrafts(f.drafts);
  }, [mode, column, f.drafts]);
  useEffect(() => (mode === "board" && column === 0 ? () => putDrafts(null) : undefined), [mode, column]);
  // No project and Hide (OEM-260) too, on the main board and the archive: in the URL, and remembered for
  // this person on this device (a view's are its own, so it neither writes nor remembers them)
  const keepsProjectView = (mode === "board" || mode === "archive") && !here && column === 0;
  useEffect(() => {
    if (!keepsProjectView || viewMode) return;
    const v = { noProject: f.noProject, hideProjects: f.hideProjects };
    putProjectView(v);
    saveProjectView(viewer.id, v, archive);
  }, [keepsProjectView, viewMode, f.noProject, f.hideProjects, viewer.id, archive]);
  useEffect(() => (keepsProjectView ? () => putProjectView(null) : undefined), [keepsProjectView]);
  // ?attach=12 (or 12:<noteId>), where a redeemed upload link lands: Add screenshots on that item or note
  useEffect(() => {
    if (mode !== "board" || column !== 0) return;
    const want = readAttach(window.location.search);
    if (!want || !all.some((i) => i.num === want.num)) return;
    window.history.replaceState(window.history.state, "", window.location.pathname + withoutParam(window.location.search, "attach") + window.location.hash);
    setDialog({ kind: "attach", num: want.num, noteId: want.noteId });
  }, [mode, column, all]);
  // an item's chip was clicked: the list changes under it, so keep that card where it was on screen
  const keepAt = useRef<{ num: number; top: number } | null>(null);

  // search as you type, lightly debounced
  useEffect(() => {
    const t = setTimeout(() => setF((cur) => ({ ...cur, q: qDraft.trim() })), 130);
    return () => clearTimeout(t);
  }, [qDraft]);

  // close dropdowns on outside click / Escape
  useEffect(() => {
    const close = () => setOpen(null);
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    document.addEventListener("click", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", close);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  // which items this board has: a project's (its own board, or one picked in the Project filter), or
  // the main board - everything except projects with a board of their own and what's in them
  // an item's panel has that one item (its card, then its history); a project's panel is like its board
  const onePanel = inPanel && (panel.kind === "item" || panel.kind === "notes");
  const itemPanel = onePanel ? (all.find((i) => i.num === panel.num) ?? null) : null;
  const scopeNum = onePanel ? null : (here?.num ?? f.project);
  // a quick view's own rule (waiting for me to verify, my to-do, ...), for whoever looks
  const quickSet = useMemo(() => (viewMode && view?.quick ? new Set(quickItems(view.quick, all, viewer, today).map((i) => i.num)) : null), [viewMode, view, all, viewer, today]);
  const items = useMemo(
    () =>
      onePanel
        ? itemPanel
          ? [itemPanel]
          : []
        : all.filter((it) =>
            scopeNum != null
              ? (it.num === scopeNum || it.project?.num === scopeNum) && (!quickSet || it.kind === "project" || quickSet.has(it.num))
              : viewMode
                ? // a view: every project, those with a board of their own too
                  !quickSet || it.kind === "project" || quickSet.has(it.num)
                : !ownBoardOnly(it),
          ),
    [all, scopeNum, onePanel, itemPanel, viewMode, quickSet],
  );
  const scopeItem = scopeNum != null ? (all.find((i) => i.num === scopeNum) ?? null) : null;
  const kidsOf = useMemo(() => {
    const m = new Map<number, BoardItem[]>();
    for (const it of all) if (it.parent) m.set(it.parent.num, [...(m.get(it.parent.num) ?? []), it]);
    return m;
  }, [all]);
  const kidsFn = (num: number) => kidsOf.get(num) ?? [];
  // how each item is numbered on this board (07 in its project, 01-07 elsewhere), and every
  // project number - current or left behind - back to its item, so "01-07" in a note is a link
  const labels = useMemo(() => new Map(all.map((i) => [i.num, itemNo(i, scopeNum)])), [all, scopeNum]);
  const keyIndex = useMemo(() => {
    const m = new Map<string, number>();
    for (const i of all) {
      if (i.key) m.set(i.key.label, i.num);
      for (const k of i.formerKeys) m.set(k, i.num);
    }
    return m;
  }, [all]);
  // No project and Hide (OEM-260) are the main board's and the archive's; a project's own board and the panels have neither
  const projectRow = (mode === "board" || archive) && !here && !inPanel;
  const fx = useMemo<Filters>(() => ({ ...(projectRow ? f : { ...f, noProject: false, hideProjects: [] }), ...(onePanel ? { drafts: "show" as const } : {}) }), [f, projectRow, onePanel]);
  // projects are not in the queues. The main board lists no project cards at all (its items carry
  // a chip instead, and the sidebar has each project's progress); the archive keeps a section of
  // archived projects at the top
  // drafts (OEM-286): someone else's is off the board, its counts and its queue chips unless Drafts says show (an item's own panel always shows it)
  const work = useMemo(() => items.filter((it) => it.kind !== "project" && draftShows(it, fx.drafts, viewer.id)), [items, fx.drafts, viewer.id]);
  const projects = useMemo(
    () =>
      archive
        ? items.filter(
            (it) =>
              it.kind === "project" &&
              it.num !== scopeNum &&
              (it.status === "open" || !f.lanes.size || f.lanes.has("closed")) &&
              matches(it, { ...fx, lanes: new Set(), kind: null }, true, today, viewer.name, viewer.id),
          )
        : [],
    [archive, items, f, scopeNum, today],
  );
  const allProjects = useMemo(() => all.filter((it) => it.kind === "project"), [all]);
  const shown = useMemo(() => sortItems(work.filter((it) => matches(it, fx, false, today, viewer.name, viewer.id)), f.sort), [work, f.sort, fx, today, viewer.name]);
  const aside = useMemo(
    () => (f.nondev ? [] : sortItems(work.filter((it) => it.nondev && matches(it, fx, true, today, viewer.name, viewer.id)), f.sort)),
    [work, f, today, viewer.name],
  );
  // a view's filters now, and whether they differ from what it was saved with (Save changes)
  const specNow = useMemo(() => specOf({ ...f, q: qDraft.trim() }, look.group), [f, qDraft, look.group]);
  const dirty = viewMode && !!view && !sameSpec(specNow, view.spec);
  useEffect(() => {
    if (mode === "board" && column === 0 && !here) onSpec?.(specNow);
  }, [mode, column, here, specNow, onSpec]);
  // Group by: headings over the sorted list (sorting applies within each group)
  const grouping: Exclude<GroupBy, "none"> | null = mainBoard && look.group !== "none" ? look.group : null;
  const groups = useMemo(
    () => (grouping ? groupItems(shown, grouping, (n) => all.find((i) => i.num === n && i.kind === "project")) : null),
    [grouping, shown, all],
  );
  const folds = useMemo(() => new Set(grouping ? look.folds[grouping] : []), [grouping, look]);

  const counts = useMemo(() => {
    const c = { lane: { todo: 0, review: 0, deploy: 0, verify: 0, closed: 0 } as Record<Lane, number>, verified: 0, open: 0, closed: 0, openHigh: 0, nondevOpen: 0, latest: "", tag: {} as Record<string, number>, role: {} as Record<string, number>, asg: {} as Record<string, number>, unassigned: 0, reviewed: 0, tmIn: 0, tmRes: 0, tmAny: 0, ai: 0, kind: {} as Record<string, number>, due: { overdue: 0, week: 0, later: 0, none: 0 } as Record<string, number>, noProject: 0, drafts: items.filter((i) => i.kind !== "project" && !!i.draft).length };
    for (const it of work) {
      // the Due filter's counts (OEM-267): overdue is open items only; the others any item
      if (isOverdue(it, today)) c.due.overdue!++;
      if (isDueThisWeek(it, today)) c.due.week!++;
      else if (it.due && it.due > today) c.due.later!++;
      if (!it.due) c.due.none!++;
      c.kind[it.kind ?? "none"] = (c.kind[it.kind ?? "none"] ?? 0) + 1;
      // in no project (OEM-260): what No project would show
      if (!it.project) c.noProject++;
      c[it.status]++;
      // as the queue chips filter: a fix with a PR ready for review is in PR review (flowLane)
      c.lane[flowLane(it)]++;
      if (it.verified) c.verified++;
      if (it.priority === "high" && it.status === "open") c.openHigh++;
      if (it.nondev && it.status === "open") c.nondevOpen++;
      if (it.addedAt > c.latest) c.latest = it.addedAt;
      for (const t of it.tags) c.tag[t] = (c.tag[t] ?? 0) + 1;
      if (teamsIn(it)) c.tmIn++;
      if (it.teamsResolved) c.tmRes++;
      if (teamsIn(it) || it.teamsResolved) c.tmAny++;
      if (it.reviewed) c.reviewed++;
      if (hasAi(it)) c.ai++;
      if (it.status !== "open") continue;
      // role and assignee chips count OPEN items - the number you'd hand to someone
      if (it.role) c.role[it.role] = (c.role[it.role] ?? 0) + 1;
      // an item with several people counts for each of them (OEM-285)
      for (const name of assigneeNames(it)) c.asg[name] = (c.asg[name] ?? 0) + 1;
      if (!it.assignee) c.unassigned++;
    }
    return c;
  }, [work, items, today]);

  // the assignee button: whose items to show (several at once), from a stack of avatars
  const assigneeFilter = (compact = false) => (
    <AssigneeFilter items={work} members={board.members} viewer={viewer} value={f.asg} onChange={(asg) => setF((cur) => ({ ...cur, asg }))} compact={compact} all={archive} />
  );

  const nums = useMemo(() => new Set(all.map((i) => i.num)), [all]);
  const byNum = (n: number) => all.find((i) => i.num === n);
  const inScope = useMemo(() => new Set(items.map((i) => i.num)), [items]);

  // the card of item n in this view: a board's cards carry their anchor; a panel's are marked by a slot
  const cardEl = (n: number): HTMLElement | null =>
    inPanel ? ((document.querySelector(`[data-col="${column}"] [data-card="${n}"]`)?.previousElementSibling as HTMLElement | null) ?? null) : document.getElementById(`item-${n}`);
  const panelTarget = (it: Pick<BoardItem, "num" | "kind">): PanelTarget => ({ kind: it.kind === "project" ? "project" : "item", num: it.num });

  // jump to an item: clear filters if it's hidden, then scroll and flash it; an item that's on
  // another board opens that board (in the archive, the Project filter shows it instead). In a
  // panel, an item it doesn't have opens in the next panel to its right.
  const jump = (n: number) => {
    const target = byNum(n);
    // someone else's draft (OEM-286) opened from a link: it isn't secret, so the board shows drafts to show it
    const showDraft: Partial<Filters> = target && draftHiddenFor(target, viewer.id) ? { drafts: "show" } : {};
    if (inPanel && !inScope.has(n)) {
      // (an item this view hasn't heard of yet - one just logged under this one - shows as a row here)
      if (target && onPanel) onPanel(panelTarget(target), { num: n });
      return;
    }
    if (target && !inScope.has(n)) {
      const to = boardOf(target);
      // the archive, or only the Project filter hides it: show it here; else open the board it's on
      if (archive || to === (here?.num ?? null)) {
        pendingJump.current = n;
        setQDraft("");
        setF({ ...clearedFilters(f.sort), project: archive ? to : null, ...showDraft });
        return;
      }
      if (onSwitch) return onSwitch(to, n);
    }
    if (!nums.has(n)) {
      if (onMissing) onMissing(n);
      else toast(`#${n} isn't on the board or in the archive.`);
      return;
    }
    // the main board has no project cards: a project opens in a panel beside it (its own board is ↗)
    if (mainBoard && target?.kind === "project" && onPanel) return onPanel(panelTarget(target), { num: n });
    pendingJump.current = n;
    // under a folded heading: unfold it (the card shows on the next render)
    const fold = grouping && target ? groupKey(target, grouping) : null;
    const unfold = fold != null && folds.has(fold);
    if (unfold) setLook(toggleFold(look, fold, false));
    if (unfold && shown.some((i) => i.num === n)) return;
    if (!cardEl(n)) {
      setQDraft("");
      setF({ ...clearedFilters(f.sort), ...showDraft });
    } else doJump();
  };
  const doJump = () => {
    const n = pendingJump.current;
    const el = n ? cardEl(n) : null;
    if (!el) return;
    pendingJump.current = null;
    // a card taller than the room under the sticky header lands with its head just below it
    const room = (el.closest(".pscroll, .cols[data-docked] > .col") ?? document.documentElement).clientHeight;
    el.scrollIntoView({ behavior: "smooth", block: el.getBoundingClientRect().height > room - 200 ? "start" : "center" });
    setFlash(n);
    setTimeout(() => setFlash(null), 1600);
  };
  useEffect(doJump);
  useLayoutEffect(() => {
    const k = keepAt.current;
    if (!k) return;
    keepAt.current = null;
    const el = cardEl(k.num);
    const d = el ? Math.round(el.getBoundingClientRect().top - k.top) : 0;
    if (!el || !d) return;
    // the board column scrolls by itself while a panel is docked; else the page does
    const col = el.closest<HTMLElement>(".cols[data-docked] > .col");
    if (col) col.scrollTop += d;
    else window.scrollBy({ top: d });
  });

  // sent here to show a particular item (the board hands archived items to the archive view; a
  // panel's "Scroll to it" shows its source, or the card that source sits on, in this column)
  useEffect(() => {
    if (jumpTo == null) return;
    // clear it first: a jump to an item on another board hands a new jumpTo to that board
    onJumped?.();
    if (!inPanel) {
      jump(jumpTo);
      return;
    }
    let host: number | null = jumpTo;
    for (let i = 0; host != null && !inScope.has(host) && i < 4; i++) host = byNum(host)?.parent?.num ?? null;
    if (host != null) jump(host);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jumpTo]);

  // links like /#item-142 (Teams' "Open on the board", OEM-142 on GitHub) land on that item, and
  // it stays marked (a glow) until you click somewhere else or press Esc
  const [linked, setLinked] = useState<number | null>(() => (typeof window === "undefined" ? null : linkedNum(window.location.hash)));
  useEffect(() => {
    if (inPanel || linked == null) return;
    const clear = () => {
      setLinked(null);
      if (linkedNum(window.location.hash) != null) window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search);
    };
    const onDown = (e: PointerEvent) => {
      if (!(e.target as Element | null)?.closest?.(`.card.selected`)) clear();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") clear();
    };
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [linked, inPanel]);
  useEffect(() => {
    if (inPanel) return;
    const go = () => {
      const n = linkedNum(window.location.hash);
      setLinked(n);
      if (n != null) jump(n);
    };
    go();
    window.addEventListener("hashchange", go);
    return () => window.removeEventListener("hashchange", go);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** true when it worked; a failure is shown as a toast */
  const run = async (p: Promise<unknown>) => {
    try {
      await p;
      await refresh();
      return true;
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Couldn't reach the server.");
      return false;
    }
  };

  const onAction = (it: BoardItem, a: CardAction) => {
    switch (a.kind) {
      case "react":
        return run(api.react(it.num, a.value));
      case "claim":
        return run(api.claim(it.num));
      case "unclaim":
        return run(api.unclaim(it.num));
      case "assignTo":
        // the avatars' picker: who has it now (several people, OEM-285), with the note for the next person
        return run(api.assignPeople(it.num, a.ids, "set", a.note));
      case "notesPanel":
        // the whole thread in a panel right of this column, tied to the card's notes
        return onPanel ? onPanel({ kind: "notes", num: it.num }, { num: it.num }) : setDialog({ kind: "note", num: it.num });
      case "editNote":
        return setDialog({ kind: "note", num: it.num, noteId: a.noteId });
      case "reopen":
        return run(api.setStatus(it.num, "open"));
      case "draft":
        return run(api.setDraft(it.num, a.value));
      case "nondev":
        return run(api.nondev(it.num, a.value));
      case "teamsres":
        return run(api.teamsResolved(it.num, a.value));
      case "shot":
        return setDialog({ kind: "shot", num: it.num, index: a.index });
      case "attach":
        return setDialog({ kind: "attach", num: it.num, files: a.files });
      case "removeShot":
        return removeShot(it, a.id);
      case "stage":
        return setDialog({ kind: "stage", num: it.num, to: a.to });
      case "archive":
        return run(api.setArchived(it.num, a.value)).then((ok) =>
          ok && toast(a.value ? `#${it.num} is in the archive now (Archive, at the top).` : `#${it.num} is back on the board.`),
        );
      case "addChild":
        return setDialog({ kind: "new", parent: it.num });
      case "copyToMine":
        return setDialog({ kind: "copy", num: it.num });
      case "bridgeLink":
      case "bridgeCreate":
      case "bridgeSend":
        return setDialog({ kind: a.kind, num: it.num });
      case "copyText": {
        // GitHub: the branch name, the reference, or the PR description with a link back here
        const text = a.pr ? prDescription(it, window.location.origin) : a.text;
        return navigator.clipboard.writeText(text).then(
          () => toast(`Copied ${a.what}: ${a.pr ? githubRef(it.num) : text}`),
          () => setDialog({ kind: "report", text }),
        );
      }
      case "linkPr":
      case "projectSettings":
      case "due":
        return setDialog({ kind: a.kind, num: it.num });
      case "setDue":
        return run(api.setDue(it.num, a.due)).then((ok) => ok && toast(a.due ? `${labels.get(it.num) ?? `#${it.num}`} is due ${a.due}.` : `Cleared the due date of ${labels.get(it.num) ?? `#${it.num}`}.`));
      case "unlinkPr":
        if (!window.confirm(`Unlink PR ${a.pr.number} (${a.pr.repo}) from ${githubRef(it.num)}? A later reference to it won't link it again; linking it by its address does.`)) return;
        return run(githubApi.unlinkPr(it.num, a.pr.id)).then((ok) => ok && toast(`Unlinked PR ${a.pr.number}.`));
      case "bridgeUnlink":
        return run(bridgeApi.unlink(it.num)).then((ok) => ok && toast(`Unlinked from OEMUAP ${it.bridge?.label ?? ""}. Notes already sent stay there.`));
      case "openProject":
        return onSwitch ? onSwitch(it.num) : setF({ ...f, project: it.num, noProject: false, hideProjects: f.hideProjects.filter((n) => n !== it.num) });
      case "panel": {
        // open it in a panel right of this column, tied to the card (or the row on it) clicked
        const target = byNum(a.num);
        if (!onPanel || !target) return jump(a.num);
        return onPanel(panelTarget(target), a.num === it.num ? { num: it.num } : { num: a.num, via: it.num });
      }
      case "filterProject": {
        // the chip: only that project's items here (the Project filter, so its chip above and Clear work as
        // usual); pressed again, everything. The card clicked stays where it is on screen.
        const el = cardEl(it.num);
        if (el) keepAt.current = { num: it.num, top: el.getBoundingClientRect().top };
        return setF((cur) => chipProject(cur, a.num));
      }
      case "goto": {
        // ↗ on a sub-item row: its board (its project's, or the main board), scrolled to it
        const t = byNum(a.num);
        const to = t ? (t.kind === "project" ? t.num : (t.project?.num ?? null)) : null;
        return t && !archive && onSwitch && to !== (here?.num ?? null) ? onSwitch(to, a.num) : jump(a.num);
      }
      case "releaseShot":
        if (!window.confirm("Show this file to the whole team? Only do this if you've checked it and it holds no password, key or token.")) return;
        return run(mediaApi.release(it.num, a.id));
      case "unlink": {
        // a link from this card's side: "03-01 blocks 01-07"
        const said = `${labels.get(it.num) ?? `#${it.num}`} ${linkLabel(a.link.kind, a.link.dir)} ${linkNo(a.link, scopeNum)}`;
        if (!window.confirm(`Remove the link "${said}"? It goes from both cards; both histories keep a line saying so.`)) return;
        return run(api.removeLink(a.link.id)).then((ok) => ok && toast(`Removed the link: ${said}.`));
      }
      default:
        return setDialog({ kind: a.kind, num: it.num });
    }
  };

  const removeShot = (it: BoardItem, id: string) => {
    const s = it.shots.find((x) => x.id === id);
    if (!window.confirm(`Remove this ${s?.kind === "video" ? "recording" : "screenshot"} from #${it.num}?`)) return;
    if (dialog?.kind === "shot") setDialog(null);
    return run(mediaApi.remove(it.num, id));
  };

  const copyReport = async () => {
    const who = f.asg.map((a) => (a === "__none" ? "Unassigned" : a));
    const text = formatReport(shown, { for: who });
    try {
      await navigator.clipboard.writeText(text);
      toast("Report copied to the clipboard.");
    } catch {
      setDialog({ kind: "report", text });
    }
  };

  const toggleLane = (l: Lane) =>
    setF((cur) => {
      const next = new Set(cur.lanes);
      if (next.has(l)) next.delete(l);
      else next.add(l);
      return { ...cur, lanes: next };
    });

  const archiveClosed = async () => {
    const n = counts.lane.closed;
    if (!window.confirm(`Move all ${n} closed item${n === 1 ? "" : "s"} to the archive? They stay searchable there, and a lead can restore any of them.`)) return;
    try {
      const r = await api.archiveClosed();
      await refresh();
      toast(`Archived ${r.count} closed item${r.count === 1 ? "" : "s"}.`);
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Couldn't reach the server.");
    }
  };
  // the Project filter: on the main board its projects; in the archive every project something archived
  // is in (the projects themselves are usually still on the board); none on a project's own board
  const pickable = useMemo(() => {
    if (here) return null;
    // a view runs over every project, so its Project filter offers every one
    if (!archive) return viewMode ? allProjects : allProjects.filter((p) => !ownBoardOnly(p));
    const m = new Map<number, Pick<BoardItem, "num" | "title" | "projectNo">>();
    for (const it of all) {
      if (it.kind === "project") m.set(it.num, it);
      else if (it.project && !m.has(it.project.num)) m.set(it.project.num, it.project);
    }
    return [...m.values()].sort((a, b) => (a.projectNo ?? Infinity) - (b.projectNo ?? Infinity) || a.num - b.num);
  }, [here, archive, all, allProjects, viewMode]);
  const tagLabel = (id: string) => TAGS.find((t) => t.id === id)?.label ?? id;
  const roleLabel = (id: string) => HIT_ROLES.find((r) => r.id === id)?.label ?? id;
  const asgLabel = (v: string) => (v === "__none" ? "Unassigned" : v);
  const kindLabel = f.kind ? (f.kind === "none" ? "Not set" : (KINDS.find((k) => k.id === f.kind)?.label ?? "")) : "";
  const projectLabel = (num: number) => {
    const p = pickable?.find((x) => x.num === num) ?? allProjects.find((x) => x.num === num);
    return p?.projectNo != null ? `${projectCode(p.projectNo)} ${p.title}` : `#${num}`;
  };
  const nFilters = activeFilterCount(f, !here);
  const onlyClosed = !archive && can(viewer, "archive") && f.lanes.size === 1 && f.lanes.has("closed") && counts.lane.closed > 0;
  // a view always shows its line (its name, and Save changes once a filter changed)
  const anyActive = nFilters > 0 || onlyClosed || viewMode;
  const resetToView = () => {
    if (!view) return;
    setQDraft(view.spec.q);
    setF({ ...initialFilters(), ...view.spec, lanes: new Set(view.spec.lanes), asg: [...view.spec.asg] });
    setLook({ ...look, group: view.spec.group });
  };

  const current = dialog && "num" in dialog ? byNum(dialog.num) : undefined;
  const featureHome = allProjects.filter((p) => p.ownBoard && p.status === "open").sort((a, b) => a.num - b.num)[0] ?? null;
  // feature requests for the board itself land in the first project with a board of its own; else a GitHub issue
  const requestFeature = personal
    ? undefined
    : featureHome && can(viewer, "create")
      ? () => setDialog({ kind: "new", parent: featureHome.num, feature: true })
      : can(viewer, "request")
        ? () => setDialog({ kind: "request" })
        : undefined;
  const menuAct = (fn: () => void) => () => {
    setOpen(null);
    fn();
  };
  const toggleWho = () =>
    setWhoOpen((cur) => {
      writeOrgSetting("buyoem.who", cur ? "0" : "1");
      return !cur;
    });

  // the header line: what this board is, and its counts (the old masthead's numbers)
  const chips: CountChip[] = archive
    ? [
        { n: items.length, label: "archived", lane: "closed" },
        { n: counts.verified, label: "verified", lane: "done" },
        { n: counts.closed - counts.verified, label: "closed unverified" },
        ...(counts.open ? [{ n: counts.open, label: "archived while open" }] : []),
      ]
    : boardChips({ ...counts.lane, review: personal ? undefined : counts.lane.review, high: counts.openHigh }, scopeItem && here ? scopeItem.children : null);
  const reviewBit = personal ? "" : ` · ${counts.lane.review} in PR review`;
  const chipsTitle = archive
    ? `${items.length} archived item${items.length === 1 ? "" : "s"}: ${counts.verified} verified, ${counts.closed - counts.verified} closed without verification`
    : here
      ? `${scopeItem?.children.done ?? 0} of ${scopeItem?.children.total ?? 0} done · ${counts.lane.todo} to do${reviewBit} · ${counts.lane.deploy} to deploy · ${counts.lane.verify} to verify · ${counts.openHigh} high priority open · ${counts.lane.closed} closed`
      : `${items.length + board.archivedCount} items logged · ${counts.lane.todo} to do${reviewBit} · ${counts.lane.deploy} to deploy · ${counts.lane.verify} to verify · ${counts.openHigh} high priority open · ${counts.lane.closed} closed · ${board.archivedCount} archived · last entry ${fmtDate(counts.latest)}`;
  // the toolbar's count (on a phone it is at the foot of the Filters popover instead)
  const countText = `${shown.length} of ${work.length}`;
  const countTip = `Showing ${shown.length} of ${work.length} ${archive ? "archived items" : "items"}${archive ? "" : ` · ${counts.reviewed} of ${work.length} reviewed`}`;
  // a project's own board: its numbers, and the ones kept for items that left it
  const range = here && scopeItem && !archive && !inPanel ? projectRange(scopeItem, all) : null;

  // the main board and the archive say an item's project with a chip on its card (under a Group by
  // project heading the heading says it); a project's own board and the panels keep the place line
  const chipped = (mainBoard || archive) && !inPanel;
  // a top-level card, then the slot that marks it for the bridge and "Scroll to it"
  // `copy`: a second showing of a card (Group by person: an item with several people is under each of them,
  // OEM-285); the first is the one links and "Scroll to it" find
  const cardOf = (it: BoardItem, isHere = false, mark: "chip" | "heading" | undefined = chipped ? "chip" : undefined, copy = false) => (
    <Fragment key={isHere ? `here-${it.num}` : it.num}>
      <Card
        item={it}
        viewer={viewer}
        flash={flash === it.num}
        selected={!inPanel && linked === it.num}
        onAction={onAction}
        notes={f.notes}
        kidsOf={kidsFn}
        itemOf={byNum}
        members={board.members}
        here={isHere}
        scope={scopeNum}
        anchor={!inPanel && !copy}
        inPanel={inPanel && panel.kind === "item" && it.num === panel.num}
        projectMark={isHere ? undefined : mark}
        chipOn={f.project != null && it.project?.num === f.project}
      />
      {!copy && <Slot num={it.num} />}
    </Fragment>
  );

  // the dialogs a card's buttons open, the same in a panel as on the board
  const dialogs = (
    <>
      {dialog?.kind === "new" && (
        <NewItemModal
          parent={dialog.parent != null ? (byNum(dialog.parent) ?? null) : null}
          feature={dialog.feature}
          presetKind={dialog.project ? "project" : null}
          viewer={viewer}
          onClose={() => setDialog(null)}
          onCreated={(n) => {
            // scroll to it once it renders; only clear filters if they hide it
            pendingJump.current = n;
            refresh().then(() => requestAnimationFrame(() => pendingJump.current === n && jump(n)));
            toast(`Logged as #${n}.`);
          }}
        />
      )}
      {dialog?.kind === "request" && <RequestModal items={all} onClose={() => setDialog(null)} toast={toast} />}
      {dialog?.kind === "report" && (
        <Modal onClose={() => setDialog(null)} wide>
          <h3>Report for this view</h3>
          <textarea rows={16} readOnly value={dialog.text} className="code" onFocus={(e) => e.currentTarget.select()} />
          <div className="row">
            <button className="btn primary" onClick={() => setDialog(null)}>
              Done
            </button>
          </div>
        </Modal>
      )}
      {current && dialog?.kind === "note" && <NoteModal item={current} editing={dialog.noteId} viewer={viewer} onClose={() => setDialog(null)} onSaved={refresh} />}
      {current && dialog?.kind === "edit" && <EditItemModal item={current} onClose={() => setDialog(null)} onSaved={refresh} />}
      {current && dialog?.kind === "close" && <CloseModal item={current} onClose={() => setDialog(null)} onSaved={refresh} />}
      {current && dialog?.kind === "stage" && (
        <StageModal
          item={current}
          to={dialog.to}
          viewer={viewer}
          members={board.members}
          onClose={() => setDialog(null)}
          onSaved={(r) => {
            refresh();
            if (r.archived) toast(`#${current.num} is verified and in the archive now.`);
          }}
        />
      )}
      {current && dialog?.kind === "history" && <HistoryModal item={current} onClose={() => setDialog(null)} />}
      {current && dialog?.kind === "link" && (
        <AddLinkModal
          item={current}
          items={all}
          onClose={() => setDialog(null)}
          onSaved={(said) => {
            refresh();
            toast(`Linked: ${said}.`);
          }}
        />
      )}
      {current && dialog?.kind === "organize" && (
        <OrganizeModal
          item={current}
          items={all}
          viewer={viewer}
          onClose={() => setDialog(null)}
          onSaved={(moves) => {
            refresh();
            if (moves.length) toast(describeMoves(moves));
          }}
        />
      )}
      {current && dialog?.kind === "shot" && (
        <Lightbox item={current} start={dialog.index} viewer={viewer} onRemove={(id) => removeShot(current, id)} onClose={() => setDialog(null)} />
      )}
      {current && dialog?.kind === "attach" && (
        <AttachModal item={current} initialFiles={dialog.files} noteId={dialog.noteId} onClose={() => setDialog(null)} onSaved={refresh} />
      )}
      {current && dialog?.kind === "copy" && boards?.list && boards.org.kind === "company" && (
        <CopyFromCompanyModal
          item={current}
          mySlug={boards.list.boards.find((b) => b.mine && b.exists && b.kind === "personal")?.slug ?? ""}
          onClose={() => setDialog(null)}
          onDone={(r) => toast(`Copied to your board as #${r.num}. Nothing changed here on OEMUAP.`)}
        />
      )}
      {current && dialog?.kind === "bridgeLink" && (
        <LinkModal
          item={current}
          onClose={() => setDialog(null)}
          onDone={(r) => {
            refresh();
            toast(`Linked to OEMUAP ${r.label}. Nothing changed there.`);
          }}
        />
      )}
      {current && dialog?.kind === "bridgeSend" && (
        <SendUpdatesModal
          item={current}
          me={viewer.name}
          onClose={() => setDialog(null)}
          onDone={(r) => {
            refresh();
            toast(r.already ? `Already posted on OEMUAP ${r.label}: nothing posted twice.` : `Posted on OEMUAP ${r.label}. Open it from the On OEMUAP block.`);
          }}
        />
      )}
      {current && dialog?.kind === "bridgeCreate" && (
        <CreateCompanyModal
          item={current}
          me={viewer.name}
          onClose={() => setDialog(null)}
          onDone={(r) => {
            refresh();
            toast(`Created on OEMUAP as ${r.label}${r.label === `#${r.num}` ? "" : ` (#${r.num})`}, linked to this item. Open it from the On OEMUAP block.`);
          }}
        />
      )}
      {current && dialog?.kind === "copy" && boards?.list && boards.org.kind === "personal" && (
        <CopyToMineModal
          item={current}
          mySlug={boards.list.boards.find((b) => b.mine && b.exists)?.slug ?? ""}
          canArchive={can(viewer, "archive")}
          onClose={() => setDialog(null)}
          onDone={(r) => {
            refresh();
            toast(`Copied to your board as #${r.num}. Open it there from the switcher.`);
          }}
        />
      )}
      {current && dialog?.kind === "due" && (
        <DueModal
          item={current}
          onClose={() => setDialog(null)}
          onSaved={(due) => {
            refresh();
            toast(due ? `${labels.get(current.num) ?? `#${current.num}`} is due ${due}.` : `Cleared the due date of ${labels.get(current.num) ?? `#${current.num}`}.`);
          }}
        />
      )}
      {current && dialog?.kind === "linkPr" && <LinkPrModal item={current} onClose={() => setDialog(null)} onSaved={refresh} toast={toast} />}
      {current && dialog?.kind === "projectSettings" && (
        <ProjectSettingsModal
          project={current}
          viewer={viewer}
          onClose={() => {
            setDialog(null);
            refresh();
          }}
          toast={toast}
          onOpenSettings={
            boards?.openSettings
              ? () => {
                  setDialog(null);
                  boards.openSettings!();
                }
              : undefined
          }
        />
      )}
      {dialog?.kind === "platform" && <PlatformModal onClose={() => setDialog(null)} toast={toast} />}
      {dialog?.kind === "team" && (
        <TeamAdminModal
          viewer={viewer}
          onClose={() => setDialog(null)}
          onChanged={refresh}
          onJump={(n) => {
            setDialog(null);
            jump(n);
          }}
        />
      )}
    </>
  );

  if (inPanel) {
    const project = panel.kind === "project" ? scopeItem : null;
    const it = project ?? itemPanel;
    const done = project?.children ?? { done: 0, total: 0 };
    // an item's notes: the whole thread, with the composer at the bottom edge
    const notesPanel = panel.kind === "notes";
    const meta = project ? (
      <>
        <span>
          {done.done} of {done.total} done
        </span>
        <span>{counts.lane.todo} to do</span>
        <span>{counts.lane.deploy} to deploy</span>
        <span>{counts.lane.verify} to verify</span>
        <span>{counts.lane.closed} closed</span>
      </>
    ) : notesPanel && itemPanel ? (
      <>
        {threadMeta(itemPanel, f.notes).map((s) => (
          <span key={s}>{s}</span>
        ))}
      </>
    ) : itemPanel ? (
      <>
        <span>{itemPanel.project ? `in project ${boardName(itemPanel.project)}` : "in no project"}</span>
        {itemPanel.parent && itemPanel.parent.kind !== "project" && <span>sub-item of {labels.get(itemPanel.parent.num) ?? `#${itemPanel.parent.num}`}</span>}
        {itemPanel.locked && <span>locked to its project</span>}
        {itemPanel.archived && <span>in the archive</span>}
      </>
    ) : null;
    return (
      <JumpContext.Provider value={{ nums, jump, labels, keys: keyIndex }}>
        <div className="sp-sticky" data-kind={panel.kind}>
          <PanelHeader {...panel} item={it} meta={meta} onOpenAsBoard={project && panel.onOpenAsBoard ? () => panel.onOpenAsBoard!({ ...f, q: qDraft.trim() }) : undefined} />
          {project && (
            <PanelFilterRow
              q={qDraft}
              onQ={setQDraft}
              f={f}
              setF={setF}
              lanes={counts.lane}
              company={!personal}
              counts={{ ...counts, notReviewed: work.length - counts.reviewed }}
              assignee={assigneeFilter(true)}
              open={open === "filters"}
              onToggle={() => setOpen(open === "filters" ? null : "filters")}
            />
          )}
        </div>
        {notesPanel ? (
          itemPanel ? (
            <NotesThread
            item={itemPanel}
            filter={f.notes}
            onCompose={can(viewer, "note") ? () => setDialog({ kind: "note", num: itemPanel.num }) : undefined}
            onEdit={can(viewer, "note") ? (noteId) => setDialog({ kind: "note", num: itemPanel.num, noteId }) : undefined}
            shots={(noteId) => (
              <ShotStrip
                item={itemPanel}
                viewer={viewer}
                noteId={noteId}
                onOpen={(index) => setDialog({ kind: "shot", num: itemPanel.num, index })}
                onRemove={(id) => removeShot(itemPanel, id)}
                onRelease={(id) => {
                  if (window.confirm("Show this file to the whole team? Only do this if you've checked it and it holds no password, key or token.")) void run(mediaApi.release(itemPanel.num, id));
                }}
              />
            )}
          />
          ) : (
            <div className="sp-body" />
          )
        ) : (
          <div className="sp-body">
            <div className="list">
              {project
                ? shown.length || aside.length
                  ? [...shown, ...aside].map((x) => cardOf(x))
                  : <div className="empty">{childCount(project) ? "No items match these filters." : "Nothing in this project yet."}</div>
                : itemPanel && cardOf(itemPanel)}
            </div>
            {itemPanel && <HistoryList num={itemPanel.num} version={JSON.stringify(itemPanel)} shots={itemPanel.shots} />}
          </div>
        )}
        {dialogs}
      </JumpContext.Provider>
    );
  }

  return (
    <JumpContext.Provider value={{ nums, jump, labels, keys: keyIndex }}>
      <div className="c1-stick" data-archive={archive ? "1" : undefined}>
        <header className="mast-c1" data-project={here ? "1" : undefined}>
          <div className="wrap row1">
            {onMenu && <MenuButton open={menuOpen} onClick={onMenu} />}
            <span className="vb-brand">Buy.OEM</span>
            {boards ? (
              // the board's name is the switcher: "OEMUAP ▾ › 03 · Delivery app" (orgs.md 4.2)
              <BoardSwitcher
                crumb={archive ? "Archive" : viewMode && view ? `Views › ${view.name}` : here && scopeItem ? boardName(scopeItem) : null}
                crumbTitle={here && scopeItem ? `Project ${boardName(scopeItem)}${scopeItem.ownBoard ? " - a board of its own" : ""}` : undefined}
              />
            ) : (
              <h1 title={here && scopeItem ? `Project ${boardName(scopeItem)}${scopeItem.ownBoard ? " - a board of its own" : ""}` : undefined}>{viewMode && view ? `Views › ${view.name}` : boardName(here ? scopeItem : null, archive)}</h1>
            )}
            {personal && <PrivateChip people={listing?.people ?? []} />}
            <span className="row1-meta">
              <CountChips chips={chips} title={chipsTitle} />
              {offline && <span className="vb-offline">reconnecting&hellip;</span>}
              {!archive && (
                <button type="button" className="chip sm who-toggle" aria-expanded={whoOpen} title="Reviewer pills: who reviewed how many items" onClick={toggleWho}>
                  Who reviewed <Icon name="chevron" />
                </button>
              )}
            </span>
            <span className="acts">
              {personal && boards?.openPeople && (
                <button type="button" className="btn icon-only people-btn" aria-label="People on this board" title="People on this board: who's here, and inviting" onClick={boards.openPeople}>
                  <Icon name="people" />
                </button>
              )}
              {/* the main board's cogwheel (OEMUAP's main view, not a project's board): Settings, for admins and leads */}
              {mainBoard && !personal && boards?.openSettings && (
                <button type="button" className="btn icon-only settings-btn" aria-label="Settings" title="Settings: GitHub, the repos and how each deploys" onClick={boards.openSettings}>
                  <Icon name="settings" />
                </button>
              )}
              {!archive && can(viewer, "create") && (
                <button
                  className="btn primary"
                  title={here && scopeItem ? `Log a new item in ${scopeItem.title}` : personal ? "New item on your board" : "Log a new item"}
                  onClick={() => setDialog({ kind: "new", parent: scopeItem?.num })}
                >
                  New item{here && <span className="btn-x"> here</span>}
                </button>
              )}
              {/* For you (reminders) is OEMUAP's: a private list doesn't need nagging (orgs.md 8.2) */}
              {!personal && <MastExtras items={all} viewer={viewer} />}
              <AvatarMenu
                viewer={viewer}
                open={open === "me"}
                onToggle={() => setOpen(open === "me" ? null : "me")}
                onTeam={can(viewer, "admin") ? menuAct(() => setDialog({ kind: "team" })) : undefined}
                onPlatform={viewer.isPlatformAdmin ? menuAct(() => setDialog({ kind: "platform" })) : undefined}
                onRequest={requestFeature ? menuAct(requestFeature) : undefined}
                onSignOut={menuAct(onSignOut)}
              />
              {onReset && (
                <button type="button" className="chip sm reset-btn" title="Close every unpinned panel; back to the main board, its filters cleared" onClick={onReset}>
                  Reset
                </button>
              )}
            </span>
          </div>
          {whoOpen && !archive && (
            <div className="wrap">
              <div className="who-strip who-bar">
                {board.reviewers.length === 0 ? (
                  <span className="who-none">No reviews recorded yet.</span>
                ) : (
                  board.reviewers.map((r) => (
                    <span className="who-pill" key={`${r.name}|${r.agent?.app ?? ""}`}>
                      <Avatar name={r.name} ai={!!r.agent} />
                      <b>
                        {r.name}
                        {r.name === viewer.name && !r.agent ? " (you)" : ""}
                      </b>
                      <AiChip agent={r.agent} />
                      <span className="n">
                        {r.items} item{r.items === 1 ? "" : "s"}
                        {r.closed ? ` · ${r.closed} closed` : ""}
                      </span>
                    </span>
                  ))
                )}
              </div>
            </div>
          )}
          {!archive && (
            <div className="wrap">
              <Ribbon items={work} label={(it) => labels.get(it.num) ?? `#${it.num}`} onJump={jump} />
            </div>
          )}
        </header>
        {board.org?.frozen && !inPanel && <FrozenBanner frozen={board.org.frozen} />}

        <div className="onerow-wrap">
          <div className="wrap onerow">
            <input
              className="search"
              type="search"
              value={qDraft}
              placeholder={archive ? "Search the archive, 01-07…" : "Search titles, notes, part numbers, 01-07…"}
              aria-label="Search items"
              title="Project numbers find items too: 01-03 finds an item that has left project 01"
              onChange={(e) => setQDraft(e.target.value)}
            />
            <GoToToolbarButton />
            {!archive && (
              <span className="lanebar" role="group" aria-label="Queues">
                {lanesFor(!personal).map((l) => (
                  <button key={l.id} type="button" className="chip lane-chip" data-lane={l.id} title={`${l.label}: ${l.hint}`} aria-pressed={f.lanes.has(l.id)} onClick={() => toggleLane(l.id)}>
                    {l.label.replace(/ queue$/, "")} <span className="n">&middot; {counts.lane[l.id]}</span>
                  </button>
                ))}
              </span>
            )}
            {assigneeFilter()}
            <FiltersMenu open={open === "filters"} count={nFilters} onToggle={() => setOpen(open === "filters" ? null : "filters")}>
              <FilterPanel
                f={f}
                setF={setF}
                counts={{ ...counts, notReviewed: work.length - counts.reviewed }}
                projects={pickable}
                projectView={projectRow}
                archive={archive}
                company={!personal}
                onCopyReport={copyReport}
                onClear={nFilters ? () => setF((cur) => withoutPopoverFilters(cur)) : undefined}
                group={mainBoard ? look.group : undefined}
                onGroup={mainBoard ? (g) => setLook({ ...look, group: g }) : undefined}
                count={countText}
              />
            </FiltersMenu>
            <select className="chip sort-select" aria-label="Sort order" title="Sort order" value={f.sort} onChange={(e) => setF({ ...f, sort: e.target.value as Sort })}>
              {(Object.keys(SORT_LABEL) as Sort[]).map((s) => (
                <option key={s} value={s}>
                  {SORT_LABEL[s]}
                </option>
              ))}
            </select>
            {mainBoard && <GroupSelect value={look.group} onChange={(g) => setLook({ ...look, group: g })} />}
            <NotesToggle all={notesView.all} flipped={notesView.flipped} onToggle={notesView.toggle} />
            <span className="count-line" title={countTip}>
              {countText}
            </span>
          </div>
          {anyActive && (
            <div className="wrap fsummary">
              {viewMode && view && (
                <ViewModeBar
                  name={view.name}
                  quick={!!view.quick}
                  dirty={dirty}
                  canSave={!!view.saved?.canEdit}
                  onSaveChanges={() => onSaveView?.(specNow, "changes")}
                  onSaveNew={() => onSaveView?.(specNow, "new")}
                  onReset={resetToView}
                />
              )}
              <div className="factive">
                {f.high && (
                  <button className="achip" title="Remove this filter" onClick={() => setF({ ...f, high: false })}>
                    High &times;
                  </button>
                )}
                {f.notReviewed && (
                  <button className="achip" title="Remove this filter" onClick={() => setF({ ...f, notReviewed: false })}>
                    Not yet reviewed &times;
                  </button>
                )}
                {f.tag && (
                  <button className="achip" title="Remove this filter" onClick={() => setF({ ...f, tag: null })}>
                    {tagLabel(f.tag)} &times;
                  </button>
                )}
                {f.role && (
                  <button className="achip" title="Remove this filter" onClick={() => setF({ ...f, role: null })}>
                    {roleLabel(f.role)} &times;
                  </button>
                )}
                {f.asg.map((a) => (
                  <button key={a} className="achip" title="Remove this person from the filter" onClick={() => setF({ ...f, asg: f.asg.filter((x) => x !== a) })}>
                    {asgLabel(a)} &times;
                  </button>
                ))}
                {f.teams && (
                  <button className="achip" title="Remove this filter" onClick={() => setF({ ...f, teams: null })}>
                    {TEAMS_LABEL[f.teams]} &times;
                  </button>
                )}
                {f.nondev && (
                  <button className="achip" title="Remove this filter" onClick={() => setF({ ...f, nondev: false })}>
                    Non-dev &times;
                  </button>
                )}
                {f.kind && (
                  <button className="achip" title="Remove this filter" onClick={() => setF({ ...f, kind: null })}>
                    {kindLabel} &times;
                  </button>
                )}
                {f.due && (
                  <button className="achip" title="Remove this filter" onClick={() => setF({ ...f, due: null })}>
                    {DUE_LABEL[f.due]} &times;
                  </button>
                )}
                {!here && f.project != null && (
                  <button className="achip" title={archive ? "Back to the main board's archive" : "Back to the whole board"} onClick={() => setF({ ...f, project: null })}>
                    Project {projectLabel(f.project)} &times;
                  </button>
                )}
                {f.drafts && f.drafts !== "hide" && (
                  <button className="achip" title="Hide other people's drafts again" onClick={() => setF({ ...f, drafts: "hide" })}>
                    {f.drafts === "only" ? "Only drafts" : "Showing drafts"} &times;
                  </button>
                )}
                {projectRow && f.noProject && (
                  <button className="achip" title="Show items in projects again" onClick={() => setF({ ...f, noProject: false })}>
                    No project &times;
                  </button>
                )}
                {projectRow &&
                  f.hideProjects.map((n) => (
                    <button key={`hide-${n}`} className="achip" title="Show this project's items again" onClick={() => setF({ ...f, hideProjects: f.hideProjects.filter((x) => x !== n) })}>
                      Hiding {projectLabel(n)} &times;
                    </button>
                  ))}
                {f.notes !== "all" && (
                  <button className="achip" title="Show everyone's notes" onClick={() => setF({ ...f, notes: "all" })}>
                    Notes: {NOTES_LABEL[f.notes]} &times;
                  </button>
                )}
              </div>
              {onlyClosed && (
                <div className="count-line">
                  <button type="button" className="xref" onClick={archiveClosed}>
                    Archive all {counts.lane.closed} closed
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <main className="wrap">
        {range && scopeItem && (
          <div className="board-meta">
            <span>
              Project <b>{scopeItem.projectNo != null ? projectCode(scopeItem.projectNo) : `#${scopeItem.num}`}</b> &middot; #{scopeItem.num}
              {scopeItem.ownBoard ? " · a board of its own" : ""}
            </span>
            <span>
              {range.from ? (
                <>
                  &middot; numbers <b>{range.from}</b> to <b>{range.to}</b>
                </>
              ) : (
                <>&middot; no numbers given out yet</>
              )}
            </span>
            {range.reserved.map((r) => (
              <span key={r.key} title={`${r.key} stays reserved for #${r.num}: nobody else gets it, and it is ${r.key} again if it comes back`}>
                &middot; <b>{r.key}</b> reserved:{" "}
                <button type="button" className="xref" onClick={() => jump(r.num)}>
                  #{r.num}
                </button>{" "}
                left
              </span>
            ))}
          </div>
        )}
        <div className="list">
          {here && scopeItem && !archive && cardOf(scopeItem, true)}
          {here && scopeItem && !archive && !personal && can(viewer, "github") && <UnlinkedPrs project={scopeItem} items={all.filter((i) => i.project?.num === scopeItem.num)} onAttached={refresh} toast={toast} />}
          {projects.length > 0 && (
            <>
              <div className="section-head">
                Projects &middot; {projects.length}
              </div>
              {projects.map((it) => cardOf(it))}
              {(shown.length > 0 || aside.length > 0) && <div className="section-head">Items</div>}
            </>
          )}
          {personal && !archive && !here && !all.length && !board.archivedCount ? (
            <EmptyBoard viewer={viewer} onNewProject={() => setDialog({ kind: "new", project: true })} onNewItem={() => setDialog({ kind: "new" })} />
          ) : !shown.length && !aside.length ? (
            !projects.length && (
              <div className="empty">
                {archive && !items.length
                  ? "Nothing in the archive yet. Verified items land here."
                  : scopeItem && !childCount(scopeItem)
                    ? "Nothing in this project yet. Use New item to add the first."
                    : viewMode
                      ? "Nothing matches this view right now, in any project."
                      : "No items match these filters."}
              </div>
            )
          ) : (
            <>
              {groups
                ? (() => {
                    // by person, an item with several people shows under each of them; the first showing is its anchor
                    const seen = new Set<number>();
                    return groups.map((g) => {
                    const folded = folds.has(g.key);
                    const id = `grp-${g.by}-${g.key.replace(/\W+/g, "_")}`;
                    return (
                      <section key={`${g.by}:${g.key}`} className="grp" data-by={g.by} data-folded={folded ? "1" : undefined} aria-label={g.label}>
                        <GroupHead
                          group={g}
                          id={id}
                          folded={folded}
                          viewer={viewer}
                          onFold={() => setLook(toggleFold(look, g.key))}
                          onPanel={onPanel ? (num) => onPanel({ kind: "project", num }, { num }) : undefined}
                          onGoto={onSwitch ? (num) => onSwitch(num) : undefined}
                        />
                        {/* the project's heading is where its panel's bridge leaves from */}
                        {g.project && <Slot num={g.project.num} />}
                        <div className="grp-items" id={id} hidden={folded}>
                          {!folded &&
                            g.items.map((it) => {
                              const copy = seen.has(it.num);
                              seen.add(it.num);
                              return cardOf(it, false, g.by === "project" ? "heading" : "chip", copy);
                            })}
                        </div>
                      </section>
                    );
                  });
                  })()
                : shown.map((it) => cardOf(it))}
              {aside.length > 0 && (
                <>
                  <div className="nondev-head">
                    Non-dev &middot; {aside.length} &mdash; tracked here, not for the developers
                  </div>
                  {aside.map((it) => cardOf(it))}
                </>
              )}
            </>
          )}
        </div>
      </main>

      {dialogs}
    </JumpContext.Provider>
  );
}

/** The item a /#item-N link names, or null. Pure. */
export function linkedNum(hash: string): number | null {
  const m = hash.match(/^#item-(\d+)$/);
  return m ? Number(m[1]) : null;
}
